import { CdkPortalOutlet, type DomPortal } from '@angular/cdk/portal';
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogActions } from '@angular/material/dialog';
import { NgIcon } from '@ng-icons/core';
import { QpxButton, QpxIcon } from '../../../directives';
import { DialogContent } from '../../dialog/dialog-content';
import { DialogHeader } from '../../dialog/dialog-header';
import { DialogService } from '../../dialog/dialog.service';
import type { FilterWorkspaceController } from '../controller';

export type FilterWorkspaceAppearance = 'dialog' | 'popover';

@Component({
    selector: 'qpx-filter-workspace-surface',
    imports: [
        DialogContent,
        DialogHeader,
        CdkPortalOutlet,
        MatButtonModule,
        MatDialogActions,
        NgIcon,
        NgTemplateOutlet,
        QpxButton,
        QpxIcon,
    ],
    templateUrl: './filter-workspace-surface.html',
    styleUrl: './filter-workspace-surface.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[attr.aria-label]':
            'appearance() === "popover" ? dialogLabel() : null',
        '[attr.aria-modal]': 'appearance() === "popover" ? "true" : null',
        '[attr.id]': 'surfaceId()',
        '[attr.role]': 'appearance() === "popover" ? "dialog" : null',
        '[class.dialog]': 'appearance() === "dialog"',
        '[class.popover]': 'appearance() === "popover"',
        '[class.qpx-popover]': 'appearance() === "popover"',
    },
})
export class QpxFilterWorkspaceSurface {
    //#region Dependency injection
    private readonly dialogService = inject(DialogService);
    //#endregion

    //#region Inputs
    public readonly appearance = input.required<FilterWorkspaceAppearance>();
    public readonly content = input.required<DomPortal<HTMLElement>>();
    public readonly controller = input.required<FilterWorkspaceController>();
    public readonly dialogLabel = input.required<string>();
    public readonly hiddenDimensionKeys = input<readonly string[]>([]);
    public readonly surfaceId = input.required<string>();
    public readonly triggerLabel = input.required<string>();
    //#endregion

    //#region Outputs
    public readonly applied = output<void>();
    public readonly dismissed = output<void>();
    //#endregion

    //#region State
    protected readonly visibleSummaries = computed(() => {
        const hiddenKeys = new Set(this.hiddenDimensionKeys());

        return this.controller()
            .summaries()
            .filter((summary) => !hiddenKeys.has(summary.key));
    });
    //#endregion

    //#region Template methods
    protected apply(): void {
        this.controller().apply();

        if (this.appearance() === 'dialog') {
            this.dialogService.closeDialog(true, false);
            return;
        }

        this.applied.emit();
    }

    protected dismiss(): void {
        this.controller().cancel();

        if (this.appearance() === 'dialog') {
            this.dialogService.closeDialog(false, false);
            return;
        }

        this.dismissed.emit();
    }

    protected clearSummary(key: string, selectionKey?: string): void {
        this.controller().clearSummary(key, selectionKey);
    }

    protected resetVisibleDraft(): void {
        const summaries = this.visibleSummaries();

        for (const key of new Set(summaries.map((summary) => summary.key))) {
            this.controller().clearDimension(key);
        }
    }
    //#endregion
}
